import { useEffect, useRef, useState } from "react";
import { Flex, Box, Heading, Button, Text } from "@chakra-ui/react";
import { GiClubs, GiSpades, GiHearts, GiDiamonds } from "react-icons/gi";
import PlayerHand from "../Play/TableComponents/PlayerHand";
import { dealCards, assignInitialDealer } from "../../utils/cardUtils";
import { chooseBid } from "../../utils/ai/bidding";
import { nextSeat } from "../../utils/ai/setbackRules";
import { GameProvider } from "../../context/gameContext/gameContext";
import "../Play/TableComponents/Deck.css";

const BOT_NAMES = { P2: "Player 2", P3: "Player 3", P4: "Player 4" };
const SUIT_ICONS = {
  diamonds: GiDiamonds,
  clubs: GiClubs,
  hearts: GiHearts,
  spades: GiSpades,
};
const SUIT_ICON_COLOR = {
  diamonds: "red",
  hearts: "red",
  clubs: undefined,
  spades: undefined,
};
const capitalize = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const newDeal = (numPlayers) => {
  const dealer = assignInitialDealer(numPlayers);
  const dealtHands = dealCards(numPlayers, dealer);
  const hands = {};
  dealtHands.forEach((hand, i) => {
    hands[`P${i + 1}`] = hand;
  });
  return { dealer, hands };
};

// Builds the feedback message for all 5 branches from the requirements:
// (a) correct pass, (b) correct amount+suit, (c) correct amount/wrong suit,
// (d) recommendation was a bid but human's amount didn't match (whether
// they passed or bid the wrong amount), (e) recommendation was Pass but
// human bid instead.
const buildFeedback = (rec, decision) => {
  const humanPassed = decision.action === "Pass";
  const humanLevel = humanPassed ? null : Number(decision.action.slice(3));
  const humanSuit = decision.trump || null;

  if (rec.action === "Pass") {
    if (humanPassed) {
      return "Congrats, you made the correct decision — passing was right with this hand.";
    }
    return "Given the cards in your hand, you should have passed.";
  }

  const recLevel = Number(rec.action.slice(3));
  const recSuit = rec.trump;

  if (humanPassed) {
    return `Given the cards in your hand, you should have bid ${recLevel} in ${capitalize(
      recSuit,
    )}.`;
  }

  if (humanLevel === recLevel) {
    if (humanSuit === recSuit) {
      return "Congrats, you bid an appropriate amount and chose the correct suit.";
    }
    return `Your bid amount was correct, but given your hand, ${capitalize(
      recSuit,
    )} would have been a better trump choice than ${capitalize(humanSuit)}.`;
  }

  const suitNote =
    humanSuit === recSuit
      ? ` (Your suit choice of ${capitalize(humanSuit)} was the right call.)`
      : ` (Your suit choice of ${capitalize(humanSuit)} wasn't the right call.)`;
  return `Given the cards in your hand, you should have bid ${recLevel} in ${capitalize(
    recSuit,
  )}.${suitNote}`;
};

const PracticeBiddingGame = ({ mode, onPlayAgain, onExit }) => {
  const isCutthroat = mode === "cutthroat";
  const numPlayers = isCutthroat ? 3 : 4;

  const [{ dealer, hands }] = useState(() => newDeal(numPlayers));
  const dealerSeat = `P${dealer}`;

  const [turnToBid, setTurnToBid] = useState(() =>
    nextSeat(dealerSeat, numPlayers),
  );
  const [highestBid, setHighestBid] = useState(0);
  const [bidBadges, setBidBadges] = useState({});
  const [stealPending, setStealPending] = useState(false);
  const [originalFourBidder, setOriginalFourBidder] = useState(null);

  // "bidding" (auction in progress) -> "trump" (human bid, picking suit) ->
  // "summary" (feedback shown) -> "skipped" (bot won before human's turn).
  const [phase, setPhase] = useState("bidding");
  const [humanDecision, setHumanDecision] = useState(null);
  const [recommendation, setRecommendation] = useState(null);

  const botActingRef = useRef(false);

  const activeSeats = Array.from({ length: numPlayers }, (_, i) => `P${i + 1}`);
  const playerName = (seat) => (seat === "P1" ? "You" : BOT_NAMES[seat]);

  // Bots act automatically. Mirrors the real game's exact mechanic: bidding
  // 4 (other than on the dealer's own natural turn) immediately jumps
  // control to the dealer for a steal-or-decline decision, skipping
  // everyone else still waiting to bid - including a human who hasn't
  // acted yet. That's the entire "bot bid 4 before your turn" rule; it
  // falls out of this naturally rather than needing a separate check.
  useEffect(() => {
    if (phase !== "bidding") return;
    if (turnToBid === "P1" || botActingRef.current) return;

    botActingRef.current = true;
    const timer = setTimeout(() => {
      botActingRef.current = false;
      const decision = stealPending
        ? chooseBid({
            mySeat: turnToBid,
            myHand: hands[turnToBid],
            currentWinningBid: 3,
            numPlayers,
            isCutthroat,
          })
        : chooseBid({
            mySeat: turnToBid,
            myHand: hands[turnToBid],
            currentWinningBid: highestBid,
            numPlayers,
            isCutthroat,
          });

      if (stealPending) {
        // turnToBid is always the dealer here - that's who a steal targets.
        if (decision.action === "bid4") {
          setBidBadges((prev) => ({ ...prev, [turnToBid]: "4" }));
        }
        // Either way, the auction is over and the human never got a turn -
        // this branch is only reachable while the human hasn't acted yet.
        setPhase("skipped");
        return;
      }

      if (decision.action === "bid4") {
        setBidBadges((prev) => ({ ...prev, [turnToBid]: "4" }));
        if (turnToBid === dealerSeat) {
          // Dealer bidding 4 on their own natural turn - nobody left to
          // offer a steal to.
          setPhase("skipped");
        } else {
          setOriginalFourBidder(turnToBid);
          setHighestBid(4);
          setStealPending(true);
          setTurnToBid(dealerSeat);
        }
        return;
      }

      setBidBadges((prev) => ({
        ...prev,
        [turnToBid]:
          decision.action === "Pass" ? "Pass" : decision.action.slice(3),
      }));
      if (decision.action !== "Pass") {
        setHighestBid(Number(decision.action.slice(3)));
      }
      setTurnToBid(nextSeat(turnToBid, numPlayers));
    }, 700);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnToBid, stealPending, phase]);

  const handleHumanBid = (action) => {
    const auctionContext = stealPending ? 3 : highestBid;
    const rec = chooseBid({
      mySeat: "P1",
      myHand: hands.P1,
      currentWinningBid: auctionContext,
      numPlayers,
      isCutthroat,
    });
    setRecommendation(rec);

    if (action === "Pass") {
      setBidBadges((prev) => ({ ...prev, P1: "Pass" }));
      setHumanDecision({ action: "Pass" });
      setPhase("summary");
    } else {
      setBidBadges((prev) => ({ ...prev, P1: action.slice(3) }));
      setHumanDecision({ action });
      setPhase("trump");
    }
  };

  const handleTrumpSelect = (suit) => {
    setHumanDecision((prev) => ({ ...prev, trump: suit }));
    setPhase("summary");
  };

  const humanTurn = phase === "bidding" && turnToBid === "P1";
  const legalLevels = stealPending
    ? [4]
    : [2, 3, 4].filter((lvl) => lvl > highestBid);

  const missingSuits = ["hearts", "diamonds", "clubs", "spades"].filter(
    (suit) => !hands.P1.some((c) => c.suit === suit),
  );

  const turnHeading = () => {
    if (phase === "trump") return "Choose your trump suit";
    if (phase === "summary") return "Result";
    if (phase === "skipped") return "Round Over";
    if (stealPending) {
      return turnToBid === "P1"
        ? `${playerName(originalFourBidder)} bid 4 - steal it?`
        : `${playerName(turnToBid)} (dealer) can steal ${playerName(
            originalFourBidder,
          )}'s bid of 4...`;
    }
    return turnToBid === "P1"
      ? "Your turn to bid"
      : `${playerName(turnToBid)}'s turn to bid`;
  };

  return (
    // PlayingCard (via PlayerHand) reads useGameContext() internally for
    // its dimming logic - /practice isn't wrapped in the app's real
    // GameProvider, so this local, isolated instance exists purely to
    // satisfy that dependency. Its default currentTurn:"" keeps dimming
    // inert; nothing here touches or leaks into the real game's state.
    <GameProvider>
      <Flex direction="column" flex="1" gap="20px" p={{ base: 2, md: 4 }}>
        <Box
          className="table"
          minH={{ base: "360px", md: "480px" }}
          borderRadius="10px"
        >
          {activeSeats
            .filter((seat) => seat !== "P1")
            .map((seat) => {
              const posClass =
                seat === "P2"
                  ? "player2Game"
                  : seat === "P3"
                    ? "player3Game"
                    : "player4Game";
              return (
                <div key={seat} className={`player ${posClass}`}>
                  {bidBadges[seat] && (
                    <Box className="bidAmount">{bidBadges[seat]}</Box>
                  )}
                  <div className="nameRow">
                    <div className="name">{playerName(seat)}</div>
                  </div>
                  {dealer === Number(seat[1]) && (
                    <p className="dealer">DEALER</p>
                  )}
                </div>
              );
            })}

          <div className="player player1Game">
            {bidBadges.P1 && <Box className="bidAmount">{bidBadges.P1}</Box>}
            <div className="nameRow">
              <div className="name">You</div>
            </div>
            {dealer === 1 && <p className="dealer">DEALER</p>}
            <div className="playerHand1">
              <PlayerHand hand={hands.P1} showAllCards={false} />
            </div>
          </div>
        </Box>

        <Flex direction="column" gap="20px" alignItems="center">
          <Heading size={{ base: "md", md: "lg" }} textAlign="center">
            {turnHeading()}
          </Heading>

          {humanTurn && (
            <Flex justify="center" gap="20px" wrap="wrap">
              {legalLevels.map((lvl) => (
                <Button
                  key={lvl}
                  onClick={() => handleHumanBid(`bid${lvl}`)}
                  bg="bg.100"
                >
                  {lvl}
                </Button>
              ))}
              <Button onClick={() => handleHumanBid("Pass")} bg="bg.100">
                Pass
              </Button>
            </Flex>
          )}

          {phase === "trump" && (
            <Flex justify="center" gap="20px" wrap="wrap">
              {["diamonds", "clubs", "hearts", "spades"].map((suit) => {
                const Icon = SUIT_ICONS[suit];
                return (
                  <Button
                    key={suit}
                    onClick={() => handleTrumpSelect(suit)}
                    isDisabled={missingSuits.includes(suit)}
                    bg="bg.100"
                  >
                    <Icon color={SUIT_ICON_COLOR[suit]} size={28} />
                  </Button>
                );
              })}
            </Flex>
          )}

          {phase === "summary" && recommendation && humanDecision && (
            <Flex
              direction="column"
              gap="16px"
              align="center"
              maxW="500px"
              textAlign="center"
            >
              <Text fontSize="lg">
                {buildFeedback(recommendation, humanDecision)}
              </Text>
              <Button
                onClick={onPlayAgain}
                bg="headline.400"
                color="white"
                _hover={{ bg: "#4a919c" }}
              >
                Play Again
              </Button>
            </Flex>
          )}

          {phase === "skipped" && (
            <Flex
              direction="column"
              gap="16px"
              align="center"
              maxW="500px"
              textAlign="center"
            >
              <Text fontSize="lg">
                Unfortunately in this scenario, a bot player bid 4, and since
                you're not the dealer, you do not have the ability to bid this
                round. Please play again.
              </Text>
              <Button
                onClick={onPlayAgain}
                bg="headline.400"
                color="white"
                _hover={{ bg: "#4a919c" }}
              >
                Play Again
              </Button>
            </Flex>
          )}

          {(phase === "summary" || phase === "skipped") && (
            <Button variant="link" color="headline.500" onClick={onExit}>
              &larr; Choose a different mode
            </Button>
          )}
        </Flex>
      </Flex>
    </GameProvider>
  );
};

export default PracticeBiddingGame;
